import CleaningAlerts from "@/components/CleaningAlerts";
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { STATUS_COLORS, CLEANING_RING } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Search, Wifi, Snowflake, Users, Bed, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

const SEVERITY_RANK = { critical: 0, major: 1, minor: 2, ok: 3, inactive: 4 };
const PARIS_CENTER = [48.8566, 2.3522];

function distFromCenter(a) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(a.latitude - PARIS_CENTER[0]);
  const dLon = toRad(a.longitude - PARIS_CENTER[1]);
  const h = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(PARIS_CENTER[0])) * Math.cos(toRad(a.latitude)) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(h));
}

// cycle definitions: list of sort steps; last click returns to default (null)
const SORT_CYCLES = {
  name: [
    (a, b) => b.name.localeCompare(a.name, "fr"),              // clic 1 : Z→A
    (a, b) => a.name.localeCompare(b.name, "fr"),              // clic 2 : A→Z
  ],
  address: [
    (a, b) => (a.city || "").localeCompare(b.city || "", "fr"),
    (a, b) => (parseInt(a.arrondissement) || 99) - (parseInt(b.arrondissement) || 99),
    (a, b) => distFromCenter(a) - distFromCenter(b),
    (a, b) => (a.country || "").localeCompare(b.country || "", "fr"),
  ],
  status: [
    (a, b) => SEVERITY_RANK[a.color_status] - SEVERITY_RANK[b.color_status],
    (a, b) => SEVERITY_RANK[b.color_status] - SEVERITY_RANK[a.color_status],
  ],
  anomalies: [
    (a, b) => (a.open_problems || 0) - (b.open_problems || 0), // croissant
    (a, b) => (b.open_problems || 0) - (a.open_problems || 0), // décroissant
  ],
  capacity: [
    (a, b) => (b.max_guests || 0) - (a.max_guests || 0),       // décroissant
    (a, b) => (a.max_guests || 0) - (b.max_guests || 0),       // croissant
  ],
  cleaning: [
    (a, b) => (a.cleaning_company || "").localeCompare(b.cleaning_company || "", "fr"),
    (a, b) => (b.cleaning_company || "").localeCompare(a.cleaning_company || "", "fr"),
  ],
  referent: [
    (a, b) => (a.referent || "").localeCompare(b.referent || "", "fr"),
    (a, b) => (b.referent || "").localeCompare(a.referent || "", "fr"),
  ],
  type: [
    (a, b) => (b.mobility_lease ? 1 : 0) - (a.mobility_lease ? 1 : 0), // BM en haut
    (a, b) => (a.mobility_lease ? 1 : 0) - (b.mobility_lease ? 1 : 0), // BM en bas
  ],
};

function Th({ label, sortKey, sort, onSort, className = "" }) {
  const active = sort.key === sortKey;
  const step = active ? sort.step : -1;
  const steps = SORT_CYCLES[sortKey].length;
  return (
    <th className={`text-left p-3 select-none ${className}`}>
      <button onClick={() => onSort(sortKey)} data-testid={`sort-${sortKey}`}
        className={`flex items-center gap-1 uppercase tracking-wide text-xs hover:text-slate-900 transition-colors ${active ? "text-slate-900 font-bold" : "text-slate-600"}`}>
        {label}
        {active ? (step === steps - 1 ? <ArrowUpDown className="w-3 h-3" /> : step === 0 ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />)
                : <ArrowUpDown className="w-3 h-3 opacity-40" />}
      </button>
    </th>
  );
}

export default function Apartments() {
  const [apts, setApts] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: null, step: 0 });
  const nav = useNavigate();
  const { user } = useAuth();
  const showMgmt = ["admin", "account_manager", "assistant_manager"].includes(user?.role);

  useEffect(() => {
    api.get("/apartments").then((r) => setApts(r.data)).catch(() => {});
  }, []);

  const onSort = (key) => {
    setSort((s) => {
      if (s.key !== key) return { key, step: 0 };
      const next = s.step + 1;
      if (next >= SORT_CYCLES[key].length) return { key: null, step: 0 }; // retour défaut
      return { key, step: next };
    });
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let list = apts;
    if (s) {
      list = list.filter((a) =>
        `${a.name} ${a.address} ${a.postal_code} ${a.owner_name || ""}`.toLowerCase().includes(s));
    }
    if (sort.key) {
      list = [...list].sort(SORT_CYCLES[sort.key][sort.step]);
    }
    return list;
  }, [apts, q, sort]);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-display text-slate-900">Appartements</h1>
          <p className="text-sm text-slate-500 mt-1">{filtered.length} logements gérés</p>
        </div>
      </div>

      <div className="mb-4"><CleaningAlerts /></div>

      <div className="mb-4 bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-2 shadow-sm">
        <Search className="w-4 h-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…"
               data-testid="apt-search"
               className="border-0 focus-visible:ring-0 shadow-none" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <Th label="Nom" sortKey="name" sort={sort} onSort={onSort} />
                <Th label="Type" sortKey="type" sort={sort} onSort={onSort} />
                <Th label="Adresse" sortKey="address" sort={sort} onSort={onSort} className="hidden md:table-cell" />
                <Th label="État" sortKey="status" sort={sort} onSort={onSort} />
                <Th label="Anomalies" sortKey="anomalies" sort={sort} onSort={onSort} />
                <Th label="Capacité" sortKey="capacity" sort={sort} onSort={onSort} className="hidden lg:table-cell" />
                <th className="text-left p-3 text-xs uppercase tracking-wide text-slate-600 hidden lg:table-cell">Équipements</th>
                {showMgmt && <Th label="Ménage" sortKey="cleaning" sort={sort} onSort={onSort} className="hidden md:table-cell" />}
                {showMgmt && <Th label="Référent" sortKey="referent" sort={sort} onSort={onSort} className="hidden md:table-cell" />}
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => {
                const status = STATUS_COLORS[a.color_status] || STATUS_COLORS.ok;
                const clean = CLEANING_RING[a.cleaning_company];
                return (
                  <tr key={a.id} onClick={() => nav(`/apartments/${a.id}`)}
                      data-testid={`apt-row-${a.id}`}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                    <td className="p-3">
                      <div className="font-medium text-slate-900">{a.name}</div>
                      <div className="text-xs text-slate-500 md:hidden">{a.arrondissement}</div>
                    </td>
                    <td className="p-3" data-testid={`apt-type-${a.id}`}>
                      {a.mobility_lease
                        ? <Badge className="bg-sky-50 text-sky-700 border-sky-200 border">BM</Badge>
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="p-3 hidden md:table-cell text-slate-600">{a.address}, {a.arrondissement}</td>
                    <td className="p-3">
                      <Badge className={`${status.cls} border`}>{status.label}</Badge>
                    </td>
                    <td className="p-3 font-medium">{a.open_problems || 0}</td>
                    <td className="p-3 hidden lg:table-cell text-slate-600">
                      <div className="flex gap-2 items-center">
                        <Users className="w-3.5 h-3.5" />{a.max_guests}
                        <Bed className="w-3.5 h-3.5 ml-2" />{a.sleepings}
                      </div>
                    </td>
                    <td className="p-3 hidden lg:table-cell">
                      <div className="flex gap-1.5" data-testid={`equip-${a.id}`}>
                        <Wifi className={`w-3.5 h-3.5 ${a.wifi_available ? "text-emerald-500" : "text-slate-300"}`} />
                        <Snowflake className={`w-3.5 h-3.5 ${a.ac_available ? "text-sky-500" : "text-slate-300"}`} />
                        <ArrowUpDown className={`w-3.5 h-3.5 ${a.elevator ? "text-violet-500" : "text-slate-300"}`} />
                      </div>
                    </td>
                    {showMgmt && (
                      <>
                        <td className="p-3 hidden md:table-cell">
                          <Badge className={`${clean?.badge} border`}>{a.cleaning_company}</Badge>
                        </td>
                        <td className="p-3 hidden md:table-cell text-slate-600">{a.referent || "—"}</td>
                      </>
                    )}
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={showMgmt ? 9 : 7} className="p-10 text-center text-slate-400">Aucun appartement</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
